import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Business,
  People,
  Pets,
  LocalHospital,
  CheckCircle,
  Cancel,
  Add
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [cabinets, setCabinets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    nom_cabinet: '',
    adresse: '',
    telephone: '',
    nom_user: '',
    prenom_user: '',
    login: '',
    mdp: '',
    duree_licence_jours: 30
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [statsRes, cabinetsRes] = await Promise.all([
        axios.get(`${API_URL}/super-admin/stats`, config),
        axios.get(`${API_URL}/super-admin/cabinets`, config)
      ]);

      setStats(statsRes.data.data);
      setCabinets(cabinetsRes.data.data);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      nom_cabinet: '',
      adresse: '',
      telephone: '',
      nom_user: '',
      prenom_user: '',
      login: '',
      mdp: '',
      duree_licence_jours: 30
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/super-admin/cabinets`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Cabinet créé avec succès !');
      handleCloseDialog();
      loadData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.message || error.message));
    }
  };

  const toggleLicence = async (licenceId, currentStatus) => {
    if (!window.confirm(`${currentStatus ? 'Désactiver' : 'Activer'} cette licence ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/super-admin/licences/${licenceId}`,
        { statut: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Licence mise à jour !');
      loadData();
    } catch (error) {
      alert('Erreur: ' + error.message);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Dashboard Super Administrateur 👨‍💼
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Vue d'ensemble de tous les cabinets vétérinaires
        </Typography>
      </Box>

      {/* Statistiques globales */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Business color="primary" sx={{ mr: 1 }} />
                <Typography color="text.secondary" variant="body2">
                  Total Cabinets
                </Typography>
              </Box>
              <Typography variant="h3">{stats?.total_cabinets|| 0}</Typography>
              <Typography variant="caption" color="success.main">
                {stats?.licences_actives || 0} actifs
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <People color="secondary" sx={{ mr: 1 }} />
                <Typography color="text.secondary" variant="body2">
                  Total Utilisateurs
                </Typography>
              </Box>
              <Typography variant="h3">{stats?.total_utilisateurs || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Pets color="success" sx={{ mr: 1 }} />
                <Typography color="text.secondary" variant="body2">
                  Total Animaux
                </Typography>
              </Box>
              <Typography variant="h3">{stats?.total_animaux || 0}</Typography>
              <Typography variant="caption" color="text.secondary">
                {stats?.total_clients || 0} clients
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <LocalHospital color="error" sx={{ mr: 1 }} />
                <Typography color="text.secondary" variant="body2">
                  Total Consultations
                </Typography>
              </Box>
              <Typography variant="h3">{stats?.total_consultations || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Liste des cabinets */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Gestion des Cabinets</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenDialog}
        >
          Nouveau Cabinet
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Nom Cabinet</strong></TableCell>
              <TableCell><strong>Adresse</strong></TableCell>
              <TableCell><strong>Téléphone</strong></TableCell>
              <TableCell><strong>Admin</strong></TableCell>
              <TableCell><strong>Licence</strong></TableCell>
              <TableCell><strong>Expiration</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cabinets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Aucun cabinet trouvé
                </TableCell>
              </TableRow>
            ) : (
              cabinets.map((cabinet) => {
                const licence = cabinet.Licences?.[0];
                const admin = cabinet.Utilisateurs?.[0];
                const isActive = licence?.statut;
                const isExpired = licence && new Date(licence.date_expiration) < new Date();

                return (
                  <TableRow key={cabinet.id_cabinet} hover>
                    <TableCell>{cabinet.id_cabinet}</TableCell>
                    <TableCell>
                      <strong>{cabinet.nom_cabinet}</strong>
                    </TableCell>
                    <TableCell>{cabinet.adresse}</TableCell>
                    <TableCell>{cabinet.telephone}</TableCell>
                    <TableCell>
                      {admin ? `${admin.prenom_user} ${admin.nom_user}` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {isActive && !isExpired ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Active"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<Cancel />}
                          label={isExpired ? "Expirée" : "Inactive"}
                          color="error"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {licence ? new Date(licence.date_expiration).toLocaleDateString('fr-FR') : 'N/A'}
                    </TableCell>
                    <TableCell align="center">
                      {licence && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => toggleLicence(licence.id_licence, licence.statut)}
                        >
                          {licence.statut ? 'Désactiver' : 'Activer'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog création cabinet */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Créer un nouveau cabinet</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Typography variant="h6" gutterBottom>
              Informations du cabinet
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="nom_cabinet"
                  label="Nom du cabinet"
                  fullWidth
                  required
                  value={formData.nom_cabinet}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="adresse"
                  label="Adresse"
                  fullWidth
                  required
                  value={formData.adresse}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="telephone"
                  label="Téléphone"
                  fullWidth
                  required
                  value={formData.telephone}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Administrateur du cabinet
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  name="nom_user"
                  label="Nom"
                  fullWidth
                  required
                  value={formData.nom_user}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="prenom_user"
                  label="Prénom"
                  fullWidth
                  required
                  value={formData.prenom_user}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="login"
                  label="Login"
                  fullWidth
                  required
                  value={formData.login}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="mdp"
                  label="Mot de passe"
                  type="password"
                  fullWidth
                  required
                  value={formData.mdp}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="duree_licence_jours"
                  label="Durée de la licence (jours)"
                  type="number"
                  fullWidth
                  value={formData.duree_licence_jours}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button type="submit" variant="contained">
              Créer
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default SuperAdminDashboard;